/**
 * The world clock, as the client sees it (T103).
 *
 * **The server owns the clock** — there is one for the whole world (D41) — and
 * it resyncs every half minute. This carries the date forward between those, so
 * the HUD reads like a clock instead of stepping thirty-six game minutes at a
 * time.
 *
 * ⚠️ **It never invents time.** Before the first sync it answers `null`, and the
 * HUD shows a dash: a date the client made up is worse than no date, because the
 * player cannot tell which one they are looking at.
 */

import { worldTime, type WorldTime } from '@lofi/shared';

export class ClientWorldClock {
  /** The server's number at the last sync, and when it arrived. */
  private playedMsAtSync = 0;
  /**
   * `performance.now()` when the last sync arrived, or `null` before the first.
   *
   * ⚠️ `performance.now()`, not `Date.now()`: it is monotonic, so a laptop
   * waking up or an NTP correction cannot make the world's date go backwards.
   */
  private syncedAt: number | null = null;

  /**
   * Take the server's answer.
   *
   * ⚠️ **The server always wins, even when it moves the clock backwards.** A
   * correction is at most the drift between two clocks over half a minute, which
   * is milliseconds; keeping a local value because it was larger would be
   * building a second clock to disagree with the first.
   */
  sync(playedMs: number, at: number): void {
    this.playedMsAtSync = playedMs;
    this.syncedAt = at;
  }

  /**
   * Played milliseconds now, or `null` if the server has not said yet.
   *
   * @param at `performance.now()`
   *
   * ⚠️ **This assumes the clock is still running**, which is true exactly
   * because we are connected — the server advances the clock while anybody is
   * connected, and we are somebody. If the socket drops, this keeps counting a
   * clock that may have stopped, until the reconnect corrects it.
   */
  playedMilliseconds(at: number): number | null {
    if (this.syncedAt === null) return null;
    return this.playedMsAtSync + Math.max(0, at - this.syncedAt);
  }

  /** What day and time it is, or `null` before the first sync. */
  time(at: number): WorldTime | null {
    const playedMs = this.playedMilliseconds(at);
    return playedMs === null ? null : worldTime(playedMs);
  }
}
